/** @jsxImportSource theme-ui */
import ErrorBoundary from 'errorBoundary/ErrorBoundary';
import { useCallback, useEffect, useMemo } from 'react';
import { Provider, useDispatch } from 'react-redux';
import { Route, useLocation, useMatch } from 'react-router';
import { Routes } from 'react-router-dom';
import { HistoryRouter } from 'redux-first-history/rr6';
import { ThemeProvider } from 'theme-ui';

import 'ga/ga-service';
import ApplicationMetadataApi from 'api/ApplicationMetadataApi';
import { AppStateContext, AppStateModel } from 'api/AppStateContext';
import { showNotification } from 'app-redux/notification.saga';
import { useInsightsRouting } from 'hooks/useInsightsRouting';
import { useAmendableState } from 'hooks/useUpdatableContext';
import { computeIsMapHidden, extractTimeframeStartDate } from 'utils/routerUtils';

import { BdIcon } from 'components/_common/BdIcon';
import { Typography } from 'components/_common/Typography';
import { Navigationbar } from 'components/Navigationbar';
import { Notification } from 'components/Notification';
import { Sidebar } from 'components/Sidebar';
import { SettingsAdminView } from 'components/SidebarView/SettingsAdminView';
import { SettingsView } from 'components/SidebarView/SettingsView';

import { fetchMetadata } from 'metadata/metadata.saga';
import { fetchAllUserGroups, fetchAllUserTypes, fetchUserProfile } from 'user/user.saga';

import store, { history } from './app-redux';
import theme from './theme';

/**
 * Uses native ResizeObserver if there is one, otherwise use the polyfill.
 * This way also only imports the polyfill if it is required
 * Taken from: https://github.com/juggle/resize-observer#switching-between-native-and-polyfilled-versions
 */
(async () => {
  if ('ResizeObserver' in window === false) {
    // Loads polyfill asynchronously, only if required.
    const module = await import('@juggle/resize-observer');
    window.ResizeObserver = module.ResizeObserver;
  }
})();

declare const $;

const App = () => {
  const { navigateToDefault, setDefaultStartTime } = useInsightsRouting();
  const dispatch = useDispatch();
  const match = useMatch('/');
  const { search, pathname } = useLocation();

  useEffect(() => {
    if (match) navigateToDefault();
  }, [match]);

  useEffect(() => {
    dispatch(fetchMetadata());
  }, []);

  const appStateInitial = useMemo(
    () => ({
      isLoadingGlobal: false,
      isSidebarCollapsed: false,
      isTimeframeDropdownExpanded: false,
      isCustomTimeframeDropdownExpanded: false,
      isPanelDropdownExpanded: false,
      startDate: extractTimeframeStartDate(search)[0],
    }),
    []
  );
  const appStateContext = useAmendableState<AppStateModel>(appStateInitial);

  // Fixes issues with mobile not being big enough https://css-tricks.com/the-trick-to-viewport-units-on-mobile/
  const handleResize = useCallback(() => {
    document.documentElement.style.setProperty('--vh', `${window.innerHeight * 0.01}px`);
  }, []);

  useEffect(() => {
    window.addEventListener('resize', handleResize);
    handleResize();

    return () => window.removeEventListener('resize', handleResize);
  }, [handleResize]);

  useEffect(() => {
    const [newStartDate, isUrlParamValid] = extractTimeframeStartDate(search);
    if (!isUrlParamValid) {
      dispatch(
        showNotification({
          type: 'SHOW_ERROR_NOTIFICATION',
          payload: `Invalid start date. Currently showing last 60 days of activity.`,
        })
      );
      setDefaultStartTime();
    }

    appStateContext.amendState({
      activeRoute: pathname,
      isMapHidden: computeIsMapHidden(pathname),
      startDate: isUrlParamValid ? newStartDate : undefined,
    });
  }, [search, pathname]);

  useEffect(() => {
    appStateContext.appState.isMapHidden
      ? $('body').addClass('map-hidden')
      : $('body').removeClass('map-hidden');
  }, [appStateContext.appState.isMapHidden]);

  useEffect(() => {
    dispatch(fetchUserProfile());
    dispatch(fetchAllUserTypes());
    dispatch(fetchAllUserGroups());

    ApplicationMetadataApi.getMetadata().then(data => {
      appStateContext.amendState({ appMetadata: data });
    });
  }, []);

  return (
    <AppStateContext.Provider value={appStateContext}>
      <Notification />
      <Navigationbar />
      <div
        sx={{
          display: 'flex',
          columnGap: '16px',
          background: '#DEEBFF',
          padding: '16px',
          alignItems: 'center',
          borderBottom: '1px solid #C4D4E3',
        }}
      >
        <BdIcon name="icon-information" color="sea90" bold />
        <Typography variant="body-small-emphasized" color="sea90">
          As part of our ongoing transition to an intelligence product, we have closed down some
          pages on Insights. Nearly all the intelligence available in Insights is available in our
          portal. Please contact your Client Service Representative if you have any questions.
        </Typography>
      </div>
      <Routes>
        <Route path="/*" element={<Sidebar />} />
        <Route path="admin-settings/*" element={<SettingsAdminView />} />
        <Route path="settings/*" element={<SettingsView />} />
      </Routes>
    </AppStateContext.Provider>
  );
};

const AppWrappers = () => {
  return (
    <Provider store={store}>
      <HistoryRouter history={history}>
        <ThemeProvider theme={theme}>
          <ErrorBoundary>
            <App />
          </ErrorBoundary>
        </ThemeProvider>
      </HistoryRouter>
    </Provider>
  );
};

export default AppWrappers;
